import { useCallback } from 'react';

import { type Accept, ErrorCode, type FileRejection, useDropzone } from 'react-dropzone';
import { toast } from 'sonner';

import { useProceduralUpload } from './use-procedural-upload';

const ACCEPT: Accept = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

/** Turn dropzone rejections (wrong type, too many files) into a single toast. */
function toastRejections(rejections: FileRejection[]) {
  const codes = new Set<string>();
  for (const rejection of rejections) {
    for (const error of rejection.errors) codes.add(error.code);
  }
  if (codes.has(ErrorCode.TooManyFiles)) {
    toast.error('Please upload one timetable at a time.');
  } else if (codes.has(ErrorCode.FileInvalidType)) {
    toast.error('Unsupported file type. Please upload a PDF, DOC, or DOCX file.');
  } else {
    toast.error('This file could not be uploaded.');
  }
}

interface UseProceduralDropzoneParams {
  matterId: string;
  /** When false the dropzone is inert (e.g. view-only users). Defaults to true. */
  enabled?: boolean;
  onSubmitted?: () => void;
}

/**
 * Wraps {@link useProceduralUpload} in a react-dropzone so a file can be dropped
 * or browsed from anywhere that wants timetable upload. Centralises the accept
 * filter and the rejection/blocked toasts so every drop target behaves the same.
 */
export function useProceduralDropzone({ matterId, enabled = true, onSubmitted }: UseProceduralDropzoneParams) {
  const upload = useProceduralUpload({ matterId, enabled, onSubmitted });
  const { isDisabled, blockedReason, acceptFile } = upload;

  const onDrop = useCallback(
    (accepted: File[], rejections: FileRejection[]) => {
      // Blocked by eligibility (extraction in flight / pending suggestions) or
      // still loading — surface the reason rather than swallowing the drop.
      if (isDisabled) {
        if (blockedReason) toast.error(blockedReason);
        return;
      }
      if (rejections.length > 0) {
        toastRejections(rejections);
        return;
      }
      // acceptFile re-validates size and type, toasting on its own if needed.
      const file = accepted[0];
      if (file) acceptFile(file);
    },
    [isDisabled, blockedReason, acceptFile],
  );

  const dropzone = useDropzone({
    accept: ACCEPT,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    useFsAccessApi: false,
    disabled: !enabled,
    onDrop,
  });

  return { ...dropzone, upload };
}
